import { scrollLock, scrollUnlock } from 'ui/scrollLock';
import { debounce } from 'utils/debounce';

/* eslint-disable no-unused-expressions, curly, no-useless-return , no-magic-numbers */
class Header {

    /**
     * @param {HTMLElement} element
     * @param {Object} options
     */
    constructor(element, options) {
        const state = {
            direction: 'down',
            status: 'top',
            previousY: 0,
            previousRatio: 0,
            init: false,
            isDesktop: false,
            isTablet: false,
            menuIsOpen: false
        };

        const _options = {
            mobileBreakpoint: 768,
            headerBreakpoint: 1470,
            debounceTime: 100
        };

        this._state = state;
        this._element = element;
        this._options = { ...options, ..._options };

        this._checkWindowSize = this._checkWindowSize.bind(this);
        this._scrollHandler = this._scrollHandler.bind(this);
        this._handleClickedHamburger = this._handleClickedHamburger.bind(this);
        this._handleIntersect = this._handleIntersect.bind(this);
        this._handleWindowClicked = this._handleWindowClicked.bind(this);
        this._handleLinkTab = this._handleLinkTab.bind(this);
        this._handleMobileLinkTab = this._handleMobileLinkTab.bind(this);
        this._handleHamburgerTab = this._handleHamburgerTab.bind(this);

        this._cacheSelectors();
        this._init();
    }

    /**
     *
     * Set initial state and add listeners
     *
     */
    _init() {
        this._checkWindowSize();
        this._handleSkipLinks();
        window.addEventListener('resize', debounce(() => {
            this._checkWindowSize();
        }, this._options.debounceTime));
    }

    /**
     *
     * Prevent execution for mobile screen size
     *
     */
    _checkWindowSize() {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        this._state.isDesktop = width > this._options.headerBreakpoint;
        this._state.isTablet = width > this._options.mobileBreakpoint && width < this._options.headerBreakpoint;

        this._handleListeners();
    }

    /**
     *
     * Add or remove listeners depending on state change
     *
     */
    _handleListeners() {
        !this._state.init && this._addScrollListeners();
        this._state.isDesktop && this._state.init && this._removeMobileListeners();
        !this._state.isDesktop && !this._state.init && this._addMobileListeners();
    }

    /**
     *
     * Cache elements
     *
     */
    _cacheSelectors() {
        this._body = document.body;
        this._observable = document.querySelector('.header__observer');
        this._hamburger = document.querySelector('.header__hamburger');
        this._openMenuName = document.querySelector('.header__hamburger-name');
        this._closeMenuName = document.querySelector('.header__hamburger-close-name');
        this._menu = document.querySelector('.header__navigation-wrapper');
        this._profileTrigger = document.querySelector('.header__dropdown-trigger');
        this._focusLinks = [...document.querySelectorAll('.header__navigation-wrapper .navigation-list__link, .header__navigation-wrapper .mobile-nav__link , .header__navigation-wrapper .mobile-nav__download')];
        this._footer = document.querySelector('.footer');
        this._stickyMenu = document.querySelector('#sticky-menu');
        this._skipLinks = [...document.querySelectorAll('[data-skip]')];

        // This._closeMenuLabel = this._hamburger.getAttribute('data-close-menu');
        // This._openMenuLabel = this._hamburger.getAttribute('data-open-menu');
    }


    /**
     *
     * Add scroll listener and intersection observer on desktop only
     *
     */
    _addScrollListeners() {

        if (this._state.isDesktop) {
            this._state.init = true;
        }

        document.addEventListener('scroll', this._scrollHandler);

        const observer = new IntersectionObserver(this._handleIntersect, {
            threshold: 0
        });
        this._observable && observer.observe(this._observable);

        const footerObserver = new IntersectionObserver(this._handleIntersect, {
            threshold: 0
        });
        this._footer && footerObserver.observe(this._footer);

    }

    _handleSkipLinks() {
        this._skipLinks && this._skipLinks.forEach((link, index) => {
            const section = link.closest('section');
            if (!section) return;
            const next = section.nextElementSibling;
            const nextId = next && next.getAttribute('id');
            const hashLink = nextId ? nextId : `skip-${index}`;
            !nextId && next && next.setAttribute('id', hashLink);
            link.setAttribute('href', `#${hashLink}`);
        });
    }

    /**
     *
     * Add hamburger listener on mobile only
     *
     */
    _addMobileListeners() {
        if (this._state.isDesktop) return;
        this._state.init = true;
        this._hamburger && this._hamburger.addEventListener('click', this._handleClickedHamburger);
        this._hamburger && this._hamburger.addEventListener('keydown', event => this._handleHamburgerTab(event));

        this._hamburger && document.addEventListener('click', event => this._handleWindowClicked(event));

        if (!this._focusLinks || this._focusLinks.length === 0) return;
        this._focusLinks[0] && this._focusLinks[0].addEventListener('keydown', event => {
            const backFocus = event.keyCode === 9 && event.shiftKey === true;
            backFocus && event.preventDefault();
            backFocus && this._hamburger.focus();
        });

        this._focusLinks[this._focusLinks.length - 1].addEventListener('keydown', event => this._handleMobileLinkTab(event));

        this._focusLinks[this._focusLinks.length - 2].addEventListener('keydown', event => this._handleLinkTab(event));

    }

    _handleLinkTab(event) {
        if (!this._state.isTablet) return;
        const nextFocus = event.keyCode === 9 && event.shiftKey === false;
        nextFocus && event.preventDefault();
        nextFocus && this._hamburger.focus();
    }

    _handleMobileLinkTab(event) {
        const isMobile = !this._state.isDesktop && !this._state.isTablet;
        const nextFocus = event.keyCode === 9 && event.shiftKey === false;
        if (!isMobile || !event.keyCode === 9) return;
        event.preventDefault();
        nextFocus && this._hamburger.focus();
        !nextFocus && this._focusLinks[this._focusLinks.length - 2].focus();
    }

    _handleHamburgerTab(event) {
        if (this._state.isDesktop || !this._state.menuIsOpen || event.keyCode !== 9) return;
        const isMobile = !this._state.isDesktop && !this._state.isTablet;
        event.preventDefault();
        const nextFocus = event.keyCode === 9 && event.shiftKey === false;
        const backFocus = event.keyCode === 9 && event.shiftKey === true;
        nextFocus && this._focusLinks[0].focus();
        isMobile && backFocus && this._focusLinks[this._focusLinks.length - 1].focus();
        !isMobile && backFocus && this._focusLinks[this._focusLinks.length - 3].focus();
    }

    /**
     *
     * Remove mobile listener
     *
     */
    _removeMobileListeners() {
        this._hamburger && this._hamburger.removeEventListener('click', this._handleClickedHamburger);
        document.removeEventListener('click', this._handleWindowClicked);
        this._element.setAttribute('data-open', false);
        this._body.setAttribute('data-menu-open', false);
        this._menu && this._menu.setAttribute('aria-hidden', false);
        this._state.menuIsOpen = false;
        this._state.init = false;
    }

    /**
     *
     * Set the scroll direction
     *
     */
    _scrollHandler() {

        if (this._state.menuIsOpen) return;
        const documentHeight = document.documentElement.clientHeight || window.innerHeight;
        const bodyBox = this._body.getBoundingClientRect();
        if (Math.abs(bodyBox.top) < this._state.previousY || -bodyBox.top + documentHeight > this._footer.offsetTop) {
            this._state.direction = 'up';
        } else {
            this._state.direction = 'down';
        }
        this._element.setAttribute('data-direction', this._state.direction);
        this._state.previousY = document.documentElement.scrollTop || document.body.scrollTop;
    }

    /**
     *
     * Handler for clicked hamburger
     *
     */
    _handleClickedHamburger() {
        const isOpen = !this._state.menuIsOpen;
        const menuLabel = isOpen ? this._closeMenuLabel : this._openMenuLabel;

        this._element.setAttribute('data-open', isOpen);
        this._body.setAttribute('data-menu-open', isOpen);
        this._menu.setAttribute('aria-hidden', !isOpen);
        this._hamburger.setAttribute('aria-expanded', isOpen);
        this._hamburger.setAttribute('aria-label', menuLabel);

        isOpen ? scrollLock() : scrollUnlock();
        isOpen && this._focusLinks[0].focus();
        isOpen && this._state.isTablet && this._element.setAttribute('data-status', 'scrolled');
        !isOpen && this._element.setAttribute('data-status', this._state.status);
        isOpen && this._state.isTablet && this._element.setAttribute('data-direction', 'down');
        this._body.setAttribute('data-scrolled', this._state.status);
        !isOpen && this._element.setAttribute('data-direction', this._state.direction);
        this._state.menuIsOpen = isOpen;
    }

    /**
     *
     * Handler for clicked hamburger
     * @param {event} event
     */
    _handleWindowClicked(event) {
        this._state.menuIsOpen && !event.target.closest('.header') && this._handleClickedHamburger();
    }

    _handleDocumentClicked(event) {
        // eslint-disable-next-line no-unused-expressions
        !event.target.closest('.modal') && !this._elements.modalTriggers.includes(event.target) && this._handleCloseModal();
    }

    /**
     * Intersection observer callback to determine if the page is at the top
     * @param {Array} entries Intersection observer entries
     */
    _handleIntersect(entries) {
        entries.forEach(entry => {
            const { isIntersecting, target } = entry;
            if (isIntersecting) {
                this._state.status = target.constructor.name === 'HTMLElement' ? 'bottom' : 'top';
            } else {
                this._state.status = Math.abs(this._state.previousY) < target.clientHeight ? 'top' : 'scrolled';
            }
            this._element.setAttribute('data-status', this._state.status);
            this._body.setAttribute('data-scrolled', this._state.status);
            // Fix for ie11
            this._stickyMenu && this._stickyMenu.setAttribute('data-bottom', this._state.status === 'top' && Math.abs(this._state.previousY) > document.body.clientHeight);
        });
    }
}

export default Header;
